import { Button, LoadError, Skeleton } from '~/components/ui';
import { DesignThumbnail } from '~/components/pages/notifications/templates/DesignPickerModal';
import { designName } from '~/lib/notification/designTemplates';
import { queryErrorMessage } from '~/modules/query/base';
import { useGetDesignTemplateQuery } from '~/modules/query/notification/designTemplates';

interface LinkedDesignProps {
  designTemplateId: string;
  readOnly: boolean;
  onChange: () => void;
  onRemove: () => void;
}

/** The email channel's linked design: its raster and name, with Change and Remove. */
export default function LinkedDesign({
  designTemplateId,
  readOnly,
  onChange,
  onRemove,
}: LinkedDesignProps) {
  const { data, error, isLoading, refetch } = useGetDesignTemplateQuery(designTemplateId);

  const actions = !readOnly && (
    <div className="flex gap-3">
      <Button variant="outline" size="sm" onClick={onChange} testId="template-design-change">
        Change design
      </Button>
      <Button variant="outline" size="sm" onClick={onRemove} testId="template-design-remove">
        Remove design
      </Button>
    </div>
  );

  return (
    <div className="space-y-3" data-testid="template-design">
      <p className="text-sm font-medium text-gray-700 dark:text-gray-400">Design</p>
      {error ? (
        <>
          <LoadError
            what="the linked design"
            message={
              queryErrorMessage(error) ??
              'The design library did not answer. Try again, or pick another design.'
            }
            onRetry={() => void refetch()}
          />
          {actions}
        </>
      ) : isLoading || !data ? (
        <Skeleton variant="block" className="h-80 max-w-sm rounded-xl" />
      ) : (
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
          <DesignThumbnail
            design={data}
            className="h-80 w-full max-w-sm rounded-xl border border-gray-200 dark:border-gray-800"
          />
          <div className="min-w-0 space-y-3">
            <p className="truncate text-sm font-medium text-gray-800 dark:text-white/90">
              {designName(data)}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              The design is the email body; its HTML comes from the design library when the email
              sends. Remove it to write the body here instead.
            </p>
            {actions}
          </div>
        </div>
      )}
    </div>
  );
}
